import "./zodJitless";
import "./global.css";
import * as Sentry from "@sentry/react";
import { setup as gooberSetup } from "goober";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import tinesLogo from "./assets/images/tines-logo.svg";
import tinesLogoDark from "./assets/images/tines-logo-dark.svg";
import tinesPurple from "./assets/images/tines-purple.svg";
import tinesRed from "./assets/images/tines-red.svg";
import tinesYellow from "./assets/images/tines-yellow.svg";
import { App } from "./components/App";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import type { Favicon } from "./environment";
import { config } from "./environment";
import { applyPolyfills } from "./lib/applyPolyfills";
import { sentryIgnoreErrors } from "./lib/sentryIgnoreErrors";
import { captureSignupAttribution } from "./lib/signupAttribution";
import { installBrowserNavigation } from "./lib/urls";

await applyPolyfills();

type BrowserNetworkInformation = {
  effectiveType?: string;
  rtt?: number;
};

function getBrowserDebugContext() {
  const network = (
    navigator as Navigator & { connection?: BrowserNetworkInformation }
  ).connection;
  const extra: Record<string, number> = {};
  const tags: Record<string, string> = {
    tab_hidden: String(document.visibilityState === "hidden"),
  };
  if (network?.effectiveType) {
    tags["network.effective_type"] = network.effectiveType;
  }
  if (typeof network?.rtt === "number") extra["network.rtt"] = network.rtt;
  return { extra, tags };
}

function normalizeConsoleMessage(message: string) {
  return message
    .replace(/\s*\{[\s\S]*$/, "")
    .replace(/clientID=\S+/g, "")
    .replace(/wsid=\S+/g, "")
    .replace(/runLoopCounter=\d+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

if (config.sentryDsn) {
  Sentry.init({
    dsn: config.sentryDsn,
    release: config.uiRelease,
    sendDefaultPii: true,
    attachStacktrace: true,
    ignoreErrors: sentryIgnoreErrors,
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["3b-ui"],
        behaviour: "drop-error-if-contains-third-party-frames",
      }),
    ],
    initialScope: {
      tags: { stack: config.stackName },
    },
    beforeSend: (event) => {
      const browserDebugContext = getBrowserDebugContext();
      event.extra = {
        ...event.extra,
        ...browserDebugContext.extra,
      };
      event.tags = {
        ...event.tags,
        ...browserDebugContext.tags,
      };
      if (event.logger === "console" && event.message) {
        const core = normalizeConsoleMessage(event.message);
        event.fingerprint = [core];
        event.message = core;
      }
      return event;
    },
  });
}

gooberSetup(createElement);
installBrowserNavigation();
captureSignupAttribution();

const defaultFavicons = {
  light: tinesLogo,
  dark: tinesLogoDark,
} as const;

const overrideFavicons: Record<Favicon, string> = {
  "logo-red.svg": tinesRed,
  "logo-yellow.svg": tinesYellow,
  "logo-purple.svg": tinesPurple,
};

// Set favicon based on environment
const links = document.querySelectorAll('link[rel="icon"]');
links.forEach((link) => {
  const faviconLink = link as HTMLLinkElement;
  if (config.favicon) {
    faviconLink.href = overrideFavicons[config.favicon];
    faviconLink.removeAttribute("media");
    return;
  }

  if (faviconLink.media.includes("dark")) {
    faviconLink.href = defaultFavicons.dark;
    return;
  }

  faviconLink.href = defaultFavicons.light;
});

const container = document.getElementById("root");
if (container) {
  // Sentry.reactErrorHandler doesn't touch the console, so without a DSN
  // a render-time throw leaves the page silently blank.
  const reactErrorHandler = Sentry.reactErrorHandler((error, errorInfo) => {
    console.error("[react.render]", error, errorInfo);
  });

  createRoot(container, {
    onUncaughtError: reactErrorHandler,
    // <AppErrorBoundary> already reports caught errors to Sentry; log at warn
    // level so captureConsoleIntegration (error-level only) doesn't duplicate them.
    onCaughtError: (error, errorInfo) =>
      console.warn("[react.caught]", error, errorInfo),
    onRecoverableError: reactErrorHandler,
  }).render(
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  );
}
